/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { I18n } from 'core';
import Button from 'components/Button/Button';
import CommunityCustomOnboarding from 'types/models/communityCustomOnboarding';
import { PromotorCustomOnboarding } from 'types/models';
import { SaveDataHandle } from '../../comunidades-custom-onboarding-process-page';
import styles from './comunidades-custom-onboarding-preguntas.module.scss';

type Props = {
  communityCustomOnboarding: CommunityCustomOnboarding | PromotorCustomOnboarding;
  saveDetails: (data: CommunityCustomOnboarding | PromotorCustomOnboarding) => void;
  title?: string;
};

type LangItem = { es: string; ca: string };

const emptyItem = (): LangItem => ({ es: '', ca: '' });
const norm = (s?: string) => (s ?? '').trim();

/** Deja siempre una fila vacía al final */
const ensureTrailingEmpty = (items: LangItem[]): LangItem[] => {
  const last = items[items.length - 1];
  if (!last || norm(last.es) !== '' || norm(last.ca) !== '') {
    return [...items, emptyItem()];
  }
  return items;
};

/** Limpia: trim + filtra filas completamente vacías */
const cleanItems = (items: LangItem[]): LangItem[] =>
  items.map((x) => ({ es: norm(x.es), ca: norm(x.ca) })).filter((x) => x.es !== '' || x.ca !== '');

const LangPill = ({ code }: { code: 'es' | 'ca' }) => (
  <span className={styles.langPill} aria-hidden>
    {code.toUpperCase()}
  </span>
);

/** Type guards para decidir la rama del modelo */
const isCommunity = (x: CommunityCustomOnboarding | PromotorCustomOnboarding): x is CommunityCustomOnboarding =>
  Object.prototype.hasOwnProperty.call(x ?? {}, 'personalizacionOnboarding');

const isPromotor = (x: CommunityCustomOnboarding | PromotorCustomOnboarding): x is PromotorCustomOnboarding =>
  Object.prototype.hasOwnProperty.call(x ?? {}, 'personalizacionPromotor');

/** Clone “seguro” sin mutar props */
const deepClone = <T,>(obj: T): T =>
  // Si tu entorno soporta structuredClone, úsalo directamente:
  // return structuredClone(obj);
  JSON.parse(JSON.stringify(obj));
const ComunidadesCustomOnboardingPreguntas = forwardRef<SaveDataHandle, Props>(
  ({ communityCustomOnboarding, saveDetails, title }, ref) => {
    const initialItems = useMemo<LangItem[]>(() => {
      const fromCommunity =
        (isCommunity(communityCustomOnboarding)
          ? communityCustomOnboarding?.personalizacionOnboarding?.preguntasOpcionalesEncuesta
          : undefined) ?? [];

      const fromPromotor =
        (isPromotor(communityCustomOnboarding)
          ? communityCustomOnboarding?.personalizacionPromotor?.preguntasOpcionalesEncuesta
          : undefined) ?? [];

      const source = (fromCommunity?.length ? fromCommunity : fromPromotor) ?? [];

      const hydrated = source.map((x: Partial<LangItem>) => ({
        es: String(x?.es ?? ''),
        ca: String(x?.ca ?? ''),
      }));

      return ensureTrailingEmpty(hydrated.length ? hydrated : [emptyItem()]);
    }, [communityCustomOnboarding]);

    const [items, setItems] = useState<LangItem[]>(initialItems);

    useEffect(() => {
      setItems(initialItems);
    }, [initialItems]);

    const updateItem = (index: number, field: keyof LangItem, value: string) => {
      setItems((prev) => {
        const next = prev.map((row, i) => (i === index ? { ...row, [field]: value } : row));
        return ensureTrailingEmpty(next);
      });
    };

    const removeRow = (index: number) =>
      setItems((prev) => {
        const next = prev.filter((_, i) => i !== index);
        return ensureTrailingEmpty(next.length ? next : [emptyItem()]);
      });

    useImperativeHandle(ref, () => ({
      getData: () => {
        const cleaned = cleanItems(items);

        // Clonamos el modelo entrante sin mutarlo
        const updated = deepClone(communityCustomOnboarding);

        // Escribimos en la rama correcta
        if (isCommunity(updated)) {
          updated.personalizacionOnboarding = {
            ...(updated.personalizacionOnboarding ?? {}),
            preguntasOpcionalesEncuesta: cleaned,
          };
        } else if (isPromotor(updated)) {
          updated.personalizacionPromotor = {
            ...(updated.personalizacionPromotor ?? {}),
            preguntasOpcionalesEncuesta: cleaned,
          };
        }
        saveDetails(updated);
      },
    }));

    return (
      <div className={styles.wrapper}>
        <h3 className={styles.title}>{title ?? I18n.t('ComunidadesCustomOnboardingProcess.opcionalQuestionsTitle')}</h3>

        <div
          className={styles.table}
          role="table"
          aria-label={I18n.t('ComunidadesCustomOnboardingProcess.opcionalQuestionsTitle')}
        >
          <div className={styles.header} role="row">
            <div className={styles.colIndex} role="columnheader">
              #
            </div>
            <div className={styles.colES} role="columnheader" lang="es">
              {I18n.t('ComunidadesCustomOnboardingProcess.spanish')}
            </div>
            <div className={styles.colCA} role="columnheader" lang="ca">
              {I18n.t('ComunidadesCustomOnboardingProcess.cat')}
            </div>
            <div className={styles.colActions} role="columnheader" />
          </div>

          {items.map((row, idx) => (
            <div className={styles.row} role="row" key={`q-${idx}`}>
              <div className={styles.colIndex} role="cell">
                {idx + 1}
              </div>

              <div className={styles.colES} role="cell">
                <div className={styles.inputWrap}>
                  <LangPill code="es" />
                  <input
                    className={styles.input}
                    type="text"
                    lang="es"
                    dir="ltr"
                    placeholder={I18n.t('ComunidadesCustomOnboardingProcess.placeholders.esPregunta')}
                    aria-label={I18n.t('ComunidadesCustomOnboardingProcess.labels.es')}
                    value={row.es}
                    onChange={(e) => updateItem(idx, 'es', e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.colCA} role="cell">
                <div className={styles.inputWrap}>
                  <LangPill code="ca" />
                  <input
                    className={styles.input}
                    type="text"
                    lang="ca"
                    dir="ltr"
                    placeholder={I18n.t('ComunidadesCustomOnboardingProcess.placeholders.caPregunta')}
                    aria-label={I18n.t('ComunidadesCustomOnboardingProcess.labels.ca')}
                    value={row.ca}
                    onChange={(e) => updateItem(idx, 'ca', e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.colActions} role="cell">
                <Button type="button" variant="secondary" onClick={() => removeRow(idx)} text="Eliminar" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  },
);

export default ComunidadesCustomOnboardingPreguntas;
